import { useCallback, useEffect, useState } from "react";
import { Loader2, Play, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MatchResultDialog } from "@/pages/admin/MatchResultDialog";
import { EMPTY_VIDEO, VideoSourcePicker, videoPayload, videoReady } from "@/pages/admin/VideoSourcePicker";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const inputCls = "h-10 w-full rounded-lg border border-slate-700 bg-[#0B0F19] px-3 text-sm text-white outline-none focus:border-[#FF2D78]";
const STATUS = { queued: "Queued", running: "Running", done: "Done", failed: "Failed" };
const STATUS_CLS = { queued: "border-slate-600 text-slate-300", running: "border-amber-400/60 text-amber-300", done: "border-[#FF2D78]/60 text-[#FF2D78]", failed: "border-red-500/60 text-red-400" };

const TeamPicker = ({ n, teams, value, onChange }) => {
  const [players, setPlayers] = useState([]);
  useEffect(() => {
    if (!value.team_name) return setPlayers([]);
    api.get("/admin/soccer-teams/players", { params: { team: value.team_name } }).then((r) => {
      setPlayers(r.data.players);
      onChange({ team_name: value.team_name, player_ids: r.data.players.map((p) => p.id) });
    });
  }, [value.team_name]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => onChange({ ...value, player_ids: value.player_ids.includes(id) ? value.player_ids.filter((x) => x !== id) : [...value.player_ids, id] });
  return (
    <div data-testid={`match-team-${n}`} className="rounded-xl border border-slate-800 bg-[#0B0F19]/60 p-4">
      <span className="eyebrow mb-2 block">Team {n}</span>
      <Select value={value.team_name} onValueChange={(t) => onChange({ team_name: t, player_ids: [] })}>
        <SelectTrigger data-testid={`match-team-${n}-select`} className="h-10 border-slate-700 bg-[#0B0F19]"><SelectValue placeholder="Pick a team" /></SelectTrigger>
        <SelectContent className="max-h-80 border-slate-800 bg-[#131927]">{teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      {players.length > 0 && <p className="mt-3 font-data text-[11px] text-slate-500">{value.player_ids.length} of {players.length} players selected</p>}
      <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
        {players.map((p) => (
          <label key={p.id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" data-testid={`match-team-${n}-player-${p.id}`} checked={value.player_ids.includes(p.id)} onChange={() => toggle(p.id)} className="accent-[#FF2D78]" />
            <span className="w-8 font-data text-xs text-slate-500">{p.jersey_number ? `#${p.jersey_number}` : ""}</span>
            <span className="flex-1 truncate">{p.name}</span>
            <span className="text-xs text-slate-500">{p.position}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const ChangeVideo = ({ id, onDone }) => {
  const [v, setV] = useState(EMPTY_VIDEO);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/match-analyses/${id}/video`, videoPayload(v));
      toast.success("Video updated. Press Retry to run the analysis again.");
      onDone();
    } catch (e) { setV((x) => ({ ...x, error: formatApiError(e) })); }
    setBusy(false);
  };
  return (
    <div data-testid={`match-change-video-panel-${id}`} className="w-full rounded-lg border border-slate-800 bg-[#0B0F19]/60 p-3">
      <VideoSourcePicker value={v} onChange={(x) => setV({ ...x, error: null })} testPrefix={`change-${id}`} />
      <button data-testid={`match-change-video-save-${id}`} onClick={save} disabled={!videoReady(v) || busy || v.uploading}
        className="mt-2 rounded-full bg-[#FF2D78] px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Save video</button>
    </div>
  );
};

const EMPTY = { video: EMPTY_VIDEO, date: "", label: "", max_minutes: 90, team1: { team_name: "", player_ids: [] }, team2: { team_name: "", player_ids: [] } };

export const MatchAnalysisTab = () => {
  const [teams, setTeams] = useState([]);
  const [list, setList] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [changing, setChanging] = useState(null);
  const load = useCallback(() => api.get("/admin/match-analyses").then((r) => setList(r.data)), []);
  useEffect(() => { api.get("/admin/soccer-teams").then((r) => setTeams(r.data.teams)); load(); }, [load]);
  useEffect(() => {
    if (!list?.items.some((m) => ["queued", "running"].includes(m.status))) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [list, load]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const ready = videoReady(f.video) && f.date && f.team1.player_ids.length && f.team2.player_ids.length && f.team1.team_name !== f.team2.team_name;
  const submit = async () => {
    setBusy(true);
    try {
      const { video, ...rest } = f;
      await api.post("/admin/match-analyses", { ...rest, ...videoPayload(video), label: f.label || null, max_minutes: Number(f.max_minutes) });
      toast.success("Analysis started. You can leave this page.");
      setF(EMPTY);
      load();
    } catch (e) {
      const msg = formatApiError(e);
      if (e.response?.status === 400 && f.video.mode === "link") setF((x) => ({ ...x, video: { ...x.video, error: msg } }));
      toast.error(msg);
    }
    setBusy(false);
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-8">
      {list && !list.configured && (
        <p data-testid="match-not-configured" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
          The analyzer isn't connected yet. Add the analyzer URL and Gemini key to the server secrets to enable analysis.
        </p>
      )}
      <div className="rounded-xl border border-slate-800 bg-[#0F1522] p-5">
        <h3 className="mb-4 font-display text-xl font-extrabold uppercase">New analysis</h3>
        <div className="mb-4"><VideoSourcePicker value={f.video} onChange={(v) => set("video")({ ...v, error: v.video_url !== f.video.video_url ? null : v.error })} /></div>
        <div className="grid gap-3 md:grid-cols-[1fr_1.5fr_1fr]">
          <input data-testid="match-date-input" type="date" value={f.date} onChange={(e) => set("date")(e.target.value)} className={inputCls} />
          <input data-testid="match-label-input" value={f.label} onChange={(e) => set("label")(e.target.value)} placeholder="Label (optional), e.g. Stanford vs UCLA" className={inputCls} />
          <label className="flex items-center gap-2 text-xs text-slate-400">Max min
            <input data-testid="match-max-minutes-input" type="number" min={1} max={150} value={f.max_minutes} onChange={(e) => set("max_minutes")(e.target.value)} className={inputCls} />
          </label>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <TeamPicker n={1} teams={teams} value={f.team1} onChange={set("team1")} />
          <TeamPicker n={2} teams={teams} value={f.team2} onChange={set("team2")} />
        </div>
        <button data-testid="match-analyze-button" onClick={submit} disabled={!ready || busy || f.video.uploading || !list?.configured}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FF2D78] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#E01F66] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {busy && f.video.mode === "link" ? "Checking link" : "Analyze"}
        </button>
      </div>
      <div>
        <h3 className="mb-3 font-display text-xl font-extrabold uppercase">Analysed matches</h3>
        {list && !list.items.length && <p data-testid="match-list-empty" className="text-sm text-slate-500">No matches analysed yet.</p>}
        <div className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {list?.items.map((m) => (
            <div key={m.id} data-testid={`match-row-${m.id}`} className="flex flex-wrap items-center gap-4 p-4">
              <button onClick={() => m.status === "done" && setOpen(m.id)} data-testid={`match-open-${m.id}`} className="min-w-0 flex-1 text-left">
                <div className="font-semibold text-white">{m.label || `${m.teams["Team 1"].team_name} vs ${m.teams["Team 2"].team_name}`}</div>
                <div className="font-data text-[11px] text-slate-500">
                  {m.date} · started {fmtDate(m.started_at || m.created_at)}{m.finished_at ? ` · finished ${fmtDate(m.finished_at)}` : ""}
                  {m.status === "done" ? (m.tracked_only ? ` · ${m.unmatched_count} tracked players, none named, nothing saved` : ` · ${m.matched} players saved · ${m.unmatched_count} unmatched`) : ""}
                </div>
                {m.video_source && <div className="font-data text-[11px] text-slate-500">{m.video_source.type === "upload" ? `Uploaded file: ${m.video_source.filename} · ${Math.round(m.video_source.size / 1024 ** 2)} MB · link expires ${fmtDate(m.video_source.expires_at)}` : `Link: ${m.video_source.original_url}`}</div>}
                {m.error && (
                  <div data-testid={`match-error-${m.id}`} className="mt-1 text-xs">
                    {m.error_plain && <div data-testid={`match-error-plain-${m.id}`} className="text-red-300">{m.error_plain}</div>}
                    <div className={m.error_plain ? "text-[11px] text-slate-500" : "text-red-400"}>{m.error}</div>
                  </div>
                )}
              </button>
              <span data-testid={`match-status-${m.id}`} className={`rounded-full border px-3 py-1 font-data text-[10px] uppercase tracking-wider ${m.tracked_only ? "border-amber-400/60 text-amber-300" : STATUS_CLS[m.status]}`}>
                {m.status === "running" && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}{m.status === "done" && m.tracked_only ? "Done: tracked players only" : STATUS[m.status]}
              </span>
              {["failed", "done"].includes(m.status) && (
                <button data-testid={`match-change-video-${m.id}`} onClick={() => setChanging(changing === m.id ? null : m.id)}
                  className="rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-white">Change video</button>
              )}
              {["failed", "done"].includes(m.status) && (
                <button data-testid={`match-retry-${m.id}`} onClick={() => act(() => api.post(`/admin/match-analyses/${m.id}/retry`), "Re-running analysis")}
                  className="rounded-full border border-slate-700 p-2 text-slate-300 hover:border-white" title="Retry"><RotateCcw className="h-4 w-4" /></button>
              )}
              {["failed", "done"].includes(m.status) && (
                <button data-testid={`match-delete-${m.id}`} onClick={() => window.confirm("Delete this analysis and remove it from every profile?") && act(() => api.delete(`/admin/match-analyses/${m.id}`), "Analysis deleted")}
                  className="rounded-full border border-slate-700 p-2 text-slate-400 hover:border-red-400 hover:text-red-400" title="Delete analysis"><Trash2 className="h-4 w-4" /></button>
              )}
              {changing === m.id && <ChangeVideo id={m.id} onDone={() => { setChanging(null); load(); }} />}
            </div>
          ))}
        </div>
      </div>
      <MatchResultDialog id={open} onClose={() => setOpen(null)} />
    </div>
  );
};
